/**
 * Onboarding HTTP service (PX-W6.1) — the first-organisation call behind
 * onboarding step 01.
 *
 * `POST /organisations` body `{slug, name, region, dataClassification}` →
 * `200/201 {organisation, policy, playbook}`. The backend's bootstrap arm
 * (server `TenantProvisioningService.requireOrgAdmin`) lets a caller with
 * ZERO organisation memberships provision their FIRST org and attaches them
 * as its owner. `clientSelfOnboarding` defaults true server-side (copy-over
 * batch-1 ruling), so the form omits it.
 *
 * Transport: the shared `apiClient` — the Bearer and the `{orgId}` handling
 * are the interceptor's business, never this module's.
 */

import { apiClient } from '@/services'

/** The organisation row the provisioning response carries. */
export interface ProvisionedOrganisation {
  slug: string
  name: string
  plan?: string
  status?: string
}

/** The `POST /organisations` response (`{organisation, policy, playbook}`). */
export interface ProvisionedTenant {
  organisation: ProvisionedOrganisation
  policy?: Record<string, unknown>
  playbook?: Record<string, unknown>
}

/** The create-first-org form's fields (§6.1 wire shape). */
export interface CreateOrganisationInput {
  slug: string
  name: string
  region: string
  dataClassification: string
}

/**
 * Provision the caller's first organisation.
 *
 * @returns The provisioned tenant (organisation + policy + playbook stub).
 * @throws ApiError with the backend's key on validation/refusal (mapped to
 *   i18n by the caller via the `errors:` prefix rule).
 */
export async function createOrganisation(
  input: CreateOrganisationInput
): Promise<ProvisionedTenant> {
  return apiClient.post<ProvisionedTenant>('/organisations', input)
}
